import { pgTable, text, serial, integer, boolean, timestamp, jsonb, pgEnum, numeric, date, uniqueIndex } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { relations } from "drizzle-orm";
import { z } from "zod";

// Enums
export const roleEnum = pgEnum('role', ['Developer', 'PlatformManager', 'OrgManager', 'OrgAdmin', 'HomeManager', 'HomeAdmin', 'HomeStaff']);
export const userStatusEnum = pgEnum('user_status', ['Active', 'Pending', 'Suspended', 'Archived']);
export const templateOwnerTypeEnum = pgEnum('template_owner_type', ['Global', 'Organization', 'Home']);
export const auditStatusEnum = pgEnum('audit_status', ['Draft', 'Submitted', 'Approved', 'closed', 'for_review', 'awaiting_review', 'reopened']);
export const templatePermissionScopeEnum = pgEnum('template_permission_scope', ['Organization', 'Home']);
export const actionStatusEnum = pgEnum('action_status', ['Pending', 'InProgress', 'Completed']);
export const notificationSeverityEnum = pgEnum('notification_severity', ['critical', 'warning', 'info']);
export const notificationKindEnum = pgEnum('notification_kind', ['notification', 'reminder']);
export const frequencyTypeEnum = pgEnum('frequency_type', ['once', 'daily', 'weekly', 'monthly', 'custom_interval']);
export const scopeTypeEnum = pgEnum('scope_type', ['Organization', 'Home']);
// Subscription enums removed for MVP

// Organizations table
export const organizations = pgTable("organizations", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  billing_email: text("billing_email").notNull(),
  address: text("address"), // Optional head office address
  contact_number: text("contact_number"), // Optional contact number
  status: text("status").notNull().default("Active"),
  features_json: jsonb("features_json").notNull().default('{}'),
  created_at: timestamp("created_at").defaultNow(),
});

// Regions table - for organizing homes within organizations
export const regions = pgTable("regions", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  description: text("description"),
  organization_id: integer("organization_id").references(() => organizations.id).notNull(),
  display_order: integer("display_order").notNull().default(0),
  created_at: timestamp("created_at").defaultNow(),
  updated_at: timestamp("updated_at").defaultNow(),
});

// Homes table
export const homes = pgTable("homes", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  address: text("address"), // Optional
  email: text("email"), // Optional contact email for the home
  contact_number: text("contact_number"), // Optional contact number
  organization_id: integer("organization_id").references(() => organizations.id), // Nullable to support standalone homes
  region_id: integer("region_id").references(() => regions.id), // Optional region assignment
  bed_count: integer("bed_count"), // Optional
  is_active: boolean("is_active").notNull().default(true),
  created_at: timestamp("created_at").defaultNow(),
});

// Users table
export const users = pgTable("users", {
  id: serial("id").primaryKey(),
  username: text("username").notNull().unique(),
  password_hash: text("password_hash").notNull(),
  email: text("email"), // User email for password reset and notifications
  first_name: text("first_name").notNull(),
  last_name: text("last_name").notNull(),
  job_title: text("job_title"),
  role: roleEnum("role").notNull(),
  status: userStatusEnum("status").notNull().default('Active'),
  organization_id: integer("organization_id").references(() => organizations.id),
  home_id: integer("home_id").references(() => homes.id),
  is_superadmin: boolean("is_superadmin").notNull().default(false),
  reset_password_token: text("reset_password_token"), // Hashed token for password reset
  reset_password_expires: text("reset_password_expires"), // Expiry timestamp as string
    email_notifications_enabled: boolean("email_notifications_enabled").notNull().default(true), // User preference for email notifications
    notification_preferences: jsonb("notification_preferences").notNull().default('{"audit_assignment":true,"audit_completion":true,"severe_case":true,"overdue":true}'), // Granular notification toggles
  created_at: timestamp("created_at").defaultNow(),
  updated_at: timestamp("updated_at").defaultNow(),
});

// Template categories table
export const template_categories = pgTable("template_categories", {
  id: serial("id").primaryKey(),
  name: text("name").notNull().unique(),
  description: text("description"),
  created_at: timestamp("created_at").defaultNow(),
});

// Template types table
export const template_types = pgTable("template_types", {
  id: serial("id").primaryKey(),
  name: text("name").notNull().unique(),
  description: text("description"),
  created_at: timestamp("created_at").defaultNow(),
});

// Templates table
export const templates = pgTable("templates", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  description: text("description"),
  category_id: integer("category_id").references(() => template_categories.id).notNull(),
  type_id: integer("type_id").references(() => template_types.id).notNull(),
  content: jsonb("content").notNull().default('{}'),
  version: integer("version").notNull().default(1),
  owner_type: templateOwnerTypeEnum("owner_type").notNull(),
  owner_org: integer("owner_org").references(() => organizations.id),
  owner_home: integer("owner_home").references(() => homes.id),
  is_active: boolean("is_active").notNull().default(true),
  hidden_from: jsonb("hidden_from").notNull().default('[]'),
  created_by: integer("created_by").references(() => users.id).notNull(),
  created_at: timestamp("created_at").defaultNow(),
  updated_at: timestamp("updated_at").defaultNow(),
});

// Subscription tables removed for MVP

// Template configurations table - managers configure templates with frequency and priority
export const template_configurations = pgTable("template_configurations", {
  id: serial("id").primaryKey(),
  template_id: integer("template_id").references(() => templates.id).notNull(),
  scope_type: scopeTypeEnum("scope_type").notNull(),
  scope_id: integer("scope_id").notNull(), // Organization ID or Home ID depending on scope_type
  priority: text("priority").notNull().default('medium'), // urgent, high, medium, low
  frequency_type: frequencyTypeEnum("frequency_type").notNull(),
  frequency_value: integer("frequency_value"), // Number of days for custom_interval
  start_date: date("start_date"),
  end_date: date("end_date"),
  next_due_at: date("next_due_at"),
  last_completed_at: timestamp("last_completed_at"),
  notify_before_days: integer("notify_before_days").default(3),
  is_active: boolean("is_active").notNull().default(true),
  
  // Advanced recurrence fields
  repeat_interval: integer("repeat_interval"), // Repeat every X weeks/months
  repeat_on_days: jsonb("repeat_on_days"), // Array of day numbers [0-6] for weekly (0=Sun, 6=Sat)
  monthly_pattern_type: text("monthly_pattern_type"), // 'day_of_month' or 'day_of_week'
  monthly_day_of_month: integer("monthly_day_of_month"), // Day 1-31 for day_of_month pattern
  monthly_week_of_month: integer("monthly_week_of_month"), // 1st, 2nd, 3rd, 4th, or -1 for last week
  monthly_day_of_week: integer("monthly_day_of_week"), // 0-6 (Sun-Sat) for day_of_week pattern
  end_condition_type: text("end_condition_type"), // 'never', 'after_occurrences', 'on_date'
  end_after_occurrences: integer("end_after_occurrences"), // Number of occurrences
  
  // Daily reminder time (UK timezone - Europe/London)
  daily_reminder_time: text("daily_reminder_time"), // Format: "HH:MM" in 24-hour UK local time
  
  created_by: integer("created_by").references(() => users.id).notNull(),
  created_at: timestamp("created_at").defaultNow(),
  updated_at: timestamp("updated_at").defaultNow(),
});

// Audits table
export const audits = pgTable("audits", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  description: text("description"),
  template_id: integer("template_id").references(() => templates.id).notNull(),
  template_configuration_id: integer("template_configuration_id").references(() => template_configurations.id),
  home_id: integer("home_id").references(() => homes.id).notNull(),
  created_by: integer("created_by").references(() => users.id).notNull(),
  assigned_to: integer("assigned_to").references(() => users.id),
  status: auditStatusEnum("status").notNull().default('Draft'),
  priority: text("priority").notNull().default('medium'),
  type_id: integer("type_id").references(() => template_types.id),
  audit_data: jsonb("audit_data").notNull().default('{}'),
  score: integer("score"),
  responses_json: jsonb("responses_json").notNull().default('{}'),
  due_date: date("due_date"),
  submitted_at: timestamp("submitted_at"),
  approved_at: timestamp("approved_at"),
  completed_at: timestamp("completed_at"),
  rejection_notes: text("rejection_notes"),
  rejected_at: timestamp("rejected_at"),
  reviewed_by: integer("reviewed_by").references(() => users.id),
  approved_by: integer("approved_by").references(() => users.id),
  attachments: jsonb("attachments").default('[]'),
  reopened_at: timestamp("reopened_at"),
  last_reopen_event_id: integer("last_reopen_event_id"),
  created_at: timestamp("created_at").defaultNow(),
  updated_at: timestamp("updated_at").defaultNow(),
});

// Audit reopen events table - tracks when audits are reopened with reasons
export const audit_reopen_events = pgTable("audit_reopen_events", {
  id: serial("id").primaryKey(),
  audit_id: integer("audit_id").references(() => audits.id).notNull(),
  reopened_by: integer("reopened_by").references(() => users.id).notNull(),
  reopen_reason: text("reopen_reason").notNull(),
  reviewer_notes: text("reviewer_notes"),
  notified_at: timestamp("notified_at"),
  created_at: timestamp("created_at").defaultNow(),
});

// Delegated template permissions table
export const delegated_template_permissions = pgTable("delegated_template_permissions", {
  id: serial("id").primaryKey(),
  scope_type: templatePermissionScopeEnum("scope_type").notNull(),
  scope_id: integer("scope_id").notNull(),
  granted_by: integer("granted_by").references(() => users.id).notNull(),
  created_at: timestamp("created_at").defaultNow(),
}, (table) => ({
  unique_scope: uniqueIndex('delegated_template_permissions_scope_unique').on(table.scope_type, table.scope_id)
}));

// Action items table - tracks actions required for audit items
export const action_items = pgTable("action_items", {
  id: serial("id").primaryKey(),
  audit_id: integer("audit_id").references(() => audits.id).notNull(),
  item_id: text("item_id").notNull(), // References the template item ID
  item_label: text("item_label").notNull(), // Label of the template item
  action_description: text("action_description").notNull(),
  status: actionStatusEnum("status").notNull().default('Pending'),
  assigned_to: integer("assigned_to").references(() => users.id), // Person assigned to complete the action
  created_by: integer("created_by").references(() => users.id).notNull(),
  reviewed_by: integer("reviewed_by").references(() => users.id), // Manager who reviewed the action
  secondary_reviewer: integer("secondary_reviewer").references(() => users.id), // Optional second reviewer (Home Admin or another Home Manager)
  completed_by: integer("completed_by").references(() => users.id),
  completed_at: timestamp("completed_at"),
  review_notes: text("review_notes"), // Reviewer's notes about the action
  acknowledged: boolean("acknowledged").default(false), // Manager acknowledgment that action has been actioned
  acknowledged_at: timestamp("acknowledged_at"), // When action was acknowledged as completed
  actioned_at: timestamp("actioned_at"), // When action was marked as actioned by reviewer
  created_at: timestamp("created_at").defaultNow(),
  updated_at: timestamp("updated_at").defaultNow(),
});

// Notifications table - tracks score-based audit notifications
export const notifications = pgTable("notifications", {
  id: serial("id").primaryKey(),
  user_id: integer("user_id").references(() => users.id).notNull(),
  audit_id: integer("audit_id").references(() => audits.id).notNull(),
  message: text("message").notNull(),
  title: text("title"),
  score: integer("score").notNull(), // 0-100 percentage score
  severity: notificationSeverityEnum("severity").notNull(), // critical (red), warning (yellow), info (green)
  kind: notificationKindEnum("kind").notNull().default('notification'), // notification (activity) or reminder (due dates)
  event: text("event"), // audit_approved, audit_submitted, audit_reopened, etc.
  actor_name: text("actor_name"), // Name of person who performed the action
  home_name: text("home_name"), // Name of home
  status: text("status"), // Audit status at time of notification
  read: boolean("read").notNull().default(false),
  audit_url: text("audit_url").notNull(), // e.g., "/audits/take?auditId=123"
  link: text("link"),
  entity_type: text("entity_type"),
  entity_id: integer("entity_id"),
  read_at: timestamp("read_at"),
  created_at: timestamp("created_at").defaultNow(),
});

// Audit locks table - prevents concurrent edits on the same audit
export const audit_locks = pgTable("audit_locks", {
  id: serial("id").primaryKey(),
  audit_id: integer("audit_id").references(() => audits.id).notNull(),
  locked_by: integer("locked_by").references(() => users.id).notNull(),
  locked_at: timestamp("locked_at").defaultNow().notNull(),
  expires_at: timestamp("expires_at").notNull(),
}, (table) => ({
  unique_audit: uniqueIndex('audit_locks_audit_unique').on(table.audit_id),
}));

// Good Practice items table - areas of good practice captured from audits
export const good_practice_items = pgTable("good_practice_items", {
  id: serial("id").primaryKey(),
  audit_id: integer("audit_id").references(() => audits.id).notNull(),
  home_id: integer("home_id").references(() => homes.id),
  created_by: integer("created_by").references(() => users.id).notNull(),
  item_id: text("item_id"),
  title: text("title"),
  description: text("description").notNull(),
  is_none: boolean("is_none").default(false),
  created_at: timestamp("created_at").defaultNow(),
});

// Demo enquiries table
export const demo_enquiries = pgTable("demo_enquiries", {
  id: serial("id").primaryKey(),
  full_name: text("full_name").notNull(),
  email: text("email").notNull(),
  organization_name: text("organization_name").notNull(),
  number_of_homes: integer("number_of_homes"),
  message: text("message"),
  data_consent: boolean("data_consent").notNull().default(false), // Consent to store information
  marketing_consent: boolean("marketing_consent").notNull().default(false), // Opt-in for marketing emails
  contacted: boolean("contacted").notNull().default(false), // Has been contacted
  contacted_by: integer("contacted_by").references(() => users.id), // Who contacted them
  contacted_at: timestamp("contacted_at"), // When they were contacted
  notes: text("notes"), // Internal notes about the enquiry
  created_at: timestamp("created_at").defaultNow(),
  updated_at: timestamp("updated_at").defaultNow(),
});

// Relations
export const organizationsRelations = relations(organizations, ({ many }) => ({
  homes: many(homes),
  users: many(users),
  templates: many(templates),
  regions: many(regions),
}));

export const regionsRelations = relations(regions, ({ one, many }) => ({
  organization: one(organizations, {
    fields: [regions.organization_id],
    references: [organizations.id],
  }),
  homes: many(homes),
}));

export const homesRelations = relations(homes, ({ one, many }) => ({
  organization: one(organizations, {
    fields: [homes.organization_id],
    references: [organizations.id],
  }),
  region: one(regions, {
    fields: [homes.region_id],
    references: [regions.id],
  }),
  users: many(users),
  templates: many(templates),
  audits: many(audits),
}));

export const usersRelations = relations(users, ({ one, many }) => ({
  organization: one(organizations, {
    fields: [users.organization_id],
    references: [organizations.id],
  }),
  home: one(homes, {
    fields: [users.home_id],
    references: [homes.id],
  }),
  created_templates: many(templates),
  created_audits: many(audits),
  reviewed_audits: many(audits),
  approved_audits: many(audits),
  granted_template_permissions: many(delegated_template_permissions),
  notifications: many(notifications),
}));

export const delegated_template_permissionsRelations = relations(delegated_template_permissions, ({ one }) => ({
  granted_by_user: one(users, {
    fields: [delegated_template_permissions.granted_by],
    references: [users.id],
  }),
}));

export const template_categoriesRelations = relations(template_categories, ({ many }) => ({
  templates: many(templates),
}));

export const template_typesRelations = relations(template_types, ({ many }) => ({
  templates: many(templates),
  audits: many(audits),
}));

export const templatesRelations = relations(templates, ({ one, many }) => ({
  category: one(template_categories, {
    fields: [templates.category_id],
    references: [template_categories.id],
  }),
  type: one(template_types, {
    fields: [templates.type_id],
    references: [template_types.id],
  }),
  created_by_user: one(users, {
    fields: [templates.created_by],
    references: [users.id],
  }),
  owner_organization: one(organizations, {
    fields: [templates.owner_org],
    references: [organizations.id],
  }),
  owner_home: one(homes, {
    fields: [templates.owner_home],
    references: [homes.id],
  }),
  audits: many(audits),
  configurations: many(template_configurations),
}));

export const template_configurationsRelations = relations(template_configurations, ({ one, many }) => ({
  template: one(templates, {
    fields: [template_configurations.template_id],
    references: [templates.id],
  }),
  created_by_user: one(users, {
    fields: [template_configurations.created_by],
    references: [users.id],
  }),
  audits: many(audits),
}));

// Subscription relations removed for MVP

export const auditsRelations = relations(audits, ({ one, many }) => ({
  template: one(templates, {
    fields: [audits.template_id],
    references: [templates.id],
  }),
  template_configuration: one(template_configurations, {
    fields: [audits.template_configuration_id],
    references: [template_configurations.id],
  }),
  type: one(template_types, {
    fields: [audits.type_id],
    references: [template_types.id],
  }),
  home: one(homes, {
    fields: [audits.home_id],
    references: [homes.id],
  }),
  created_by_user: one(users, {
    fields: [audits.created_by],
    references: [users.id],
  }),
  reviewed_by_user: one(users, {
    fields: [audits.reviewed_by],
    references: [users.id],
  }),
  approved_by_user: one(users, {
    fields: [audits.approved_by],
    references: [users.id],
  }),
  action_items: many(action_items),
  notifications: many(notifications),
}));

export const action_itemsRelations = relations(action_items, ({ one }) => ({
  audit: one(audits, {
    fields: [action_items.audit_id],
    references: [audits.id],
  }),
  created_by_user: one(users, {
    fields: [action_items.created_by],
    references: [users.id],
  }),
  assigned_to_user: one(users, {
    fields: [action_items.assigned_to],
    references: [users.id],
  }),
  reviewed_by_user: one(users, {
    fields: [action_items.reviewed_by],
    references: [users.id],
  }),
  completed_by_user: one(users, {
    fields: [action_items.completed_by],
    references: [users.id],
  }),
}));

export const notificationsRelations = relations(notifications, ({ one }) => ({
  user: one(users, {
    fields: [notifications.user_id],
    references: [users.id],
  }),
  audit: one(audits, {
    fields: [notifications.audit_id],
    references: [audits.id],
  }),
}));

export const auditLocksRelations = relations(audit_locks, ({ one }) => ({
  audit: one(audits, {
    fields: [audit_locks.audit_id],
    references: [audits.id],
  }),
  user: one(users, {
    fields: [audit_locks.locked_by],
    references: [users.id],
  }),
}));

// Insert schemas
export const insertOrganizationSchema = createInsertSchema(organizations).omit({
  id: true,
  created_at: true,
});

export const insertRegionSchema = createInsertSchema(regions).omit({
  id: true,
  created_at: true,
  updated_at: true,
});

export const insertHomeSchema = createInsertSchema(homes).omit({
  id: true,
  created_at: true,
});

export const insertUserSchema = createInsertSchema(users).omit({
  id: true,
  created_at: true,
  updated_at: true,
});

export const insertTemplateCategorySchema = createInsertSchema(template_categories).omit({
  id: true,
  created_at: true,
});

export const insertTemplateTypeSchema = createInsertSchema(template_types).omit({
  id: true,
  created_at: true,
});

export const insertTemplateSchema = createInsertSchema(templates).omit({
  id: true,
  created_at: true,
  updated_at: true,
}).extend({
  version: z.number().optional(), // Optional for backward compatibility with databases missing this column
});

export const insertTemplateConfigurationSchema = createInsertSchema(template_configurations).omit({
  id: true,
  created_at: true,
  updated_at: true,
  last_completed_at: true,
  created_by: true,
});

// Subscription schemas removed for MVP

export const insertAuditSchema = createInsertSchema(audits).omit({
  id: true,
  created_by: true,
  submitted_at: true,
  approved_at: true,
  completed_at: true,
  rejected_at: true,
  created_at: true,
  updated_at: true,
});

export const insertDelegatedTemplatePermissionSchema = createInsertSchema(delegated_template_permissions).omit({
  id: true,
  created_at: true,
});

export const insertActionItemSchema = createInsertSchema(action_items).omit({
  id: true,
  created_at: true,
  updated_at: true,
  completed_at: true,
});

export const insertAuditReopenEventSchema = createInsertSchema(audit_reopen_events).omit({
  id: true,
  created_at: true,
  notified_at: true,
});

export const insertNotificationSchema = createInsertSchema(notifications).omit({
  id: true,
  read_at: true,
  created_at: true,
});

export const insertDemoEnquirySchema = createInsertSchema(demo_enquiries).omit({
  id: true,
  created_at: true,
  updated_at: true,
  contacted_at: true,
});

// Auth schemas
export const loginSchema = z.object({
  username: z.string().min(1),
  password: z.string().min(1),
});

// User creation schema (accepts password, not password_hash)
export const createUserSchema = insertUserSchema.omit({ password_hash: true }).extend({
  password: z.string().min(1),
});

// Profile update schema - allow first_name, last_name, and email notifications updates
export const updateProfileSchema = z.object({
  first_name: z.string().min(1).max(100).optional(),
  last_name: z.string().min(1).max(100).optional(),
  email_notifications_enabled: z.boolean().optional(),
});

// Password change schema - require current and new password
export const changePasswordSchema = z.object({
  current_password: z.string().min(1),
  new_password: z.string().min(8).max(100),
});

// CSV Import Schemas
export const csvHomeImportRowSchema = z.object({
  home_name: z.string().trim().min(1, "Home name is required"),
  home_address: z.string().trim().optional().nullable(),
  home_email: z.string().trim().email("Invalid email").optional().nullable(),
  home_contact_number: z.string().trim().optional().nullable(),
  home_bed_count: z.string().optional().nullable().transform(val => {
    if (!val || val.trim() === '') return null;
    const num = parseInt(val.trim());
    if (isNaN(num) || num < 0) throw new Error("Bed count must be a positive number");
    return num;
  }),
  region_name: z.string().trim().optional().nullable(), // Optional region assignment
});

export const csvUserImportRowSchema = z.object({
  user_first_name: z.string().trim().min(1, "First name is required"),
  user_last_name: z.string().trim().min(1, "Last name is required"),
  user_email: z.string().trim().email("Invalid email"),
  user_job_title: z.string().trim().optional().nullable(),
  user_home_name: z.string().trim().optional().nullable(), // Blank for org-level users
  user_access_level: z.enum(["HomeManager", "HomeAdmin", "HomeStaff"], {
    errorMap: () => ({ message: "Access level must be: HomeManager, HomeAdmin, or HomeStaff" })
  }),
});

// Types
export type Organization = typeof organizations.$inferSelect;
export type InsertOrganization = z.infer<typeof insertOrganizationSchema>;

export type Region = typeof regions.$inferSelect;
export type InsertRegion = z.infer<typeof insertRegionSchema>;

export type Home = typeof homes.$inferSelect;
export type InsertHome = z.infer<typeof insertHomeSchema>;

export type User = typeof users.$inferSelect;
export type InsertUser = z.infer<typeof insertUserSchema>;

export type TemplateCategory = typeof template_categories.$inferSelect;
export type InsertTemplateCategory = z.infer<typeof insertTemplateCategorySchema>;

export type TemplateType = typeof template_types.$inferSelect;
export type InsertTemplateType = z.infer<typeof insertTemplateTypeSchema>;

export type Template = typeof templates.$inferSelect;
export type InsertTemplate = z.infer<typeof insertTemplateSchema>;

export type TemplateConfiguration = typeof template_configurations.$inferSelect;
export type InsertTemplateConfiguration = z.infer<typeof insertTemplateConfigurationSchema>;

// Subscription types removed for MVP

export type Audit = typeof audits.$inferSelect;
export type InsertAudit = z.infer<typeof insertAuditSchema>;

export type DelegatedTemplatePermission = typeof delegated_template_permissions.$inferSelect;
export type InsertDelegatedTemplatePermission = z.infer<typeof insertDelegatedTemplatePermissionSchema>;

export type ActionItem = typeof action_items.$inferSelect;
export type InsertActionItem = z.infer<typeof insertActionItemSchema>;
export type GoodPracticeItem = typeof good_practice_items.$inferSelect;

export type AuditReopenEvent = typeof audit_reopen_events.$inferSelect;
export type InsertAuditReopenEvent = z.infer<typeof insertAuditReopenEventSchema>;

export type Notification = typeof notifications.$inferSelect;
export type InsertNotification = z.infer<typeof insertNotificationSchema>;

export type AuditLock = typeof audit_locks.$inferSelect;

export type DemoEnquiry = typeof demo_enquiries.$inferSelect;
export type InsertDemoEnquiry = z.infer<typeof insertDemoEnquirySchema>;

export type LoginRequest = z.infer<typeof loginSchema>;
export type CreateUserRequest = z.infer<typeof createUserSchema>;
export type UpdateProfileRequest = z.infer<typeof updateProfileSchema>;
export type ChangePasswordRequest = z.infer<typeof changePasswordSchema>;

// Dashboard Analytics Types
export const dashboardSummarySchema = z.object({
  totalAudits: z.number(),
  completedAudits: z.number(),
  pendingAudits: z.number(),
  averageScore: z.number(),
  completionRate: z.number(),
  scoreChange: z.number().optional(), // Trend: positive or negative change
  topAuditTypes: z.array(z.object({
    name: z.string(),
    count: z.number(),
  })).optional(),
});

export type DashboardSummary = z.infer<typeof dashboardSummarySchema>;

// Export configuration
export const exportConfigSchema = z.object({
  includePieChart: z.boolean().default(true),
  includeLineGraph: z.boolean().default(true),
  includeTable: z.boolean().default(true),
  format: z.enum(['pdf', 'csv', 'xlsx']).default('pdf'),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
});